import { useEffect, useState } from 'react';
import { Drawer, List, Switch, Button, Typography, Space, App, Alert } from 'antd';
import { LeftOutlined, ToolOutlined } from '@ant-design/icons';
import { updateTenantAdmin } from 'api/tenantsAdmin';
import { MANTENCION_SETTINGS, MANTENCION_DEFAULTS } from 'assets/tenantConfigCatalog';

// Drawer de configuración por módulo (drill-down) que el super-admin abre
// desde la fila del tenant en TenantsPage. En v1 el único módulo es
// Mantención, sin interruptor maestro — cada ajuste se guarda como
// { config: { mantencion: { ...values } } } vía PATCH /tenants/admin/:id.
const MODULES = [
  {
    key: 'mantencion',
    label: 'Mantención',
    icon: <ToolOutlined />,
    settings: MANTENCION_SETTINGS,
  },
];

export default function TenantConfigDrawer({ open, tenant, onClose, onSaved }) {
  const { message } = App.useApp();
  const [activeModule, setActiveModule] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && tenant) {
      const current = tenant.config?.mantencion ?? {};
      setValues({ ...MANTENCION_DEFAULTS, ...current });
      setActiveModule(null);
      setError(null);
    }
  }, [open, tenant]);

  const toggle = (key, checked) => setValues((v) => ({ ...v, [key]: checked }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateTenantAdmin(tenant.id, { config: { mantencion: values } });
      message.success('Configuración guardada');
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.mensaje || 'No se pudo guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  const mod = MODULES.find((m) => m.key === activeModule);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={460}
      title={`Configuración · ${tenant?.organization ?? ''}`}
      destroyOnHidden
    >
      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} showIcon />}
      {!mod ? (
        <List
          dataSource={MODULES}
          renderItem={(m) => (
            <List.Item
              onClick={() => setActiveModule(m.key)}
              style={{ cursor: 'pointer' }}
              actions={[
                <Typography.Text key="chevron" type="secondary">
                  ›
                </Typography.Text>,
              ]}
            >
              <List.Item.Meta
                avatar={m.icon}
                title={m.label}
                description="Ajustes de comportamiento de este módulo"
              />
            </List.Item>
          )}
        />
      ) : (
        <div>
          <Button
            type="link"
            icon={<LeftOutlined />}
            onClick={() => setActiveModule(null)}
            style={{ paddingLeft: 0 }}
          >
            Volver a módulos
          </Button>
          <List
            dataSource={mod.settings}
            renderItem={(s) => {
              const disabled = s.dependsOn && !values[s.dependsOn];
              const checked = !!values[s.key] && !disabled;
              return (
                <List.Item
                  actions={[
                    <Switch
                      key="switch"
                      checked={checked}
                      disabled={disabled}
                      onChange={(c) => toggle(s.key, c)}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={s.label}
                    description={
                      <Space direction="vertical" size={2}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {s.efecto}
                        </Typography.Text>
                        {!checked && !disabled && (
                          <Typography.Text type="warning" style={{ fontSize: 11 }}>
                            {s.aviso}
                          </Typography.Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button type="primary" loading={saving} onClick={save}>
              Guardar
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
}
